'use client';

import { c as _c } from "react/compiler-runtime";
import { stripContentTags } from '@shared/all/contentFilter/contentFilter';
import { type InternalTarget, internalTarget } from '@shared/all/helpers/internalLink';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { FileText, LayoutGrid, Link2, User } from 'lucide-react';
import Link from 'next/link';
import { client } from '../../utils/trpc';

// A link to somewhere on Factiii, as a chat shows it: a card naming the thing
// it opens, which opens it here. LinkPreview is the card for everywhere else —
// that one reads the page's meta tags through a proxy and opens a new window,
// and a new window from the desktop app is the system browser.

type Preview = {
  title: string;
  detail: string;
};
const KIND = {
  post: {
    label: 'Post',
    Icon: FileText
  },
  space: {
    label: 'Space',
    Icon: LayoutGrid
  },
  user: {
    label: 'Profile',
    Icon: User
  },
  page: {
    label: 'Factiii',
    Icon: Link2
  }
} as const;

// A link nobody can open (deleted, private, mistyped) is a card with only its
// address on it, not a toast in the middle of a conversation.
const SILENT = {
  context: {
    silenceErrors: true
  }
};

/** What the card says before its preview loads, and when there is none. */
const fromPath = (target: InternalTarget, path: string): Preview => {
  switch (target.kind) {
    case 'post':
      return {
        title: 'Post',
        detail: path
      };
    case 'space':
      return {
        title: `s/${target.slug}`,
        detail: path
      };
    case 'user':
      return {
        title: `@${target.username}`,
        detail: path
      };
    case 'page':
      return {
        title: path,
        detail: 'factiii.com'
      };
  }
};
const loadPreview = async (target: InternalTarget): Promise<Preview | null> => {
  switch (target.kind) {
    case 'post':
      {
        const res = await unwrapFromResult(client.posts.singlePost.query({
          postId: target.postId
        }, SILENT));
        const post = res.posts[0];
        if (!post) return null;
        const body = stripContentTags(post.content).trim();
        return {
          title: post.title?.trim() || body || 'Post',
          detail: [post.space && `s/${post.space}`, `@${post.user.username}`].filter(Boolean).join(' · ')
        };
      }
    case 'space':
      {
        const space = await unwrapFromResult(client.spaces.get.query({
          slug: target.slug
        }, SILENT));
        return {
          title: space.name,
          detail: space.description?.trim() || `s/${target.slug}`
        };
      }
    case 'user':
      {
        const user = await unwrapFromResult(client.users.publicDetails.query({
          username: target.username
        }, SILENT));
        return {
          title: user.name?.trim() || `@${user.username}`,
          detail: `@${user.username}`
        };
      }
    case 'page':
      return null;
  }
};
const InternalLinkCard = t0 => {
  const $ = _c(16);
  const {
    path
  } = t0;
  const target = internalTarget(path);
  const {
    data
  } = useQuery({
    queryKey: ["internalLinkCard", path],
    queryFn: () => loadPreview(target),
    retry: false,
    staleTime: 300000
  });
  const {
    label,
    Icon
  } = KIND[target.kind];
  const shown = data ?? fromPath(target, path);
  let t1;
  if ($[0] !== Icon) {
    t1 = <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon size={18} aria-hidden={true} /></span>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="block text-[11px] font-medium uppercase tracking-wide text-textSecondary/70">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== shown.title) {
    t3 = <span className="block truncate text-sm font-emphasis text-textPrimary">{shown.title}</span>;
    $[4] = shown.title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== shown.detail) {
    t4 = <span className="block truncate text-xs text-textSecondary/70">{shown.detail}</span>;
    $[6] = shown.detail;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <span className="min-w-0 flex-1">{t2}{t3}{t4}</span>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== path || $[13] !== t1 || $[14] !== t5) {
    t6 = <Link href={path} onClick={_temp} className="mt-2 flex items-center gap-3 rounded-lg border border-ds-border-subtle bg-background-light p-2 text-left no-underline transition-colors duration-200 hover:border-primary/50">{t1}{t5}</Link>;
    $[12] = path;
    $[13] = t1;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export default InternalLinkCard;
function _temp(e) {
  return e.stopPropagation();
}